import React, { PureComponent } from 'react'
import qs from 'qs'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import cloneDeep from 'lodash/cloneDeep'

import { scrollAndHighlightElementWithId } from 'features/app/store/actions'
import { openPanel, openRequestReviewPanel } from 'features/taskPanel/store/actions'
import {
  cancelGetTeamlist,
  getTeamList,
  getTeamListWithoutDelay,
  getTeamMembers,
  dragAndDrop
} from 'features/teamlist/store/actions'
import { hasNoMember, hasNoTask } from 'features/teamlist/store/selectors'
import {
  updateTaskPartiallyById,
  toggleCompleteTaskRequest,
  patchTaskRequest,
  updateTask
} from 'features/task/store/actions'
import { get as getNotifications } from 'features/notifications/store/actions'
import { markTaskAsRead, markTeamTasksWithUpdatesAsRead } from 'features/notifications/store/actions'
import { groups } from 'features/list/constants.json'
import { findById } from 'features/list/utils'
import { constants, utils } from 'gipsy-misc'
import { teamlist } from 'gipsy-api'

const mapStateToProps = state => ({
  loaded: state.teamlist.loaded,
  teamItems: state.teamlist.items,
  activeTeamMember: state.teamlist.activeTeamMember,
  members: state.teamlist.teamMembers,
  highlightIds: state.teamlist.highlightIds,
  hasNoMember: hasNoMember(state),
  hasNoTask: hasNoTask(state),
  itemsLoaded: state.teamlist.itemsLoaded
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      findById,
      cancelGetTeamlist,
      getTeamList,
      getTeamListWithoutDelay,
      getTeamMembers,
      openPanel,
      openRequestReviewPanel,
      markTaskAsRead,
      markTeamTasksWithUpdatesAsRead,
      scrollAndHighlightElementWithId,
      toggleCompleteTaskRequest,
      updateTaskPartiallyById,
      updateTask,
      getNotifications,
      patchTaskRequest,
      dragAndDrop
    },
    dispatch
  )
})

export default function wrapper(Component) {
  class TeamlistPageContainer extends PureComponent {
    constructor(props) {
      super(props)

      const queryParams = qs.parse(this.props.location.search, { ignoreQueryPrefix: true })
      if (queryParams.scrollto) {
        this.props.actions.scrollAndHighlightElementWithId(queryParams.scrollto)
      }

      if (queryParams.open) {
        this.props.actions.openPanel(queryParams.open)
      }

      this.loadWithoutDelay()
    }

    load = () => {
      if (this.props.activeTeamMember) {
        this.props.actions.getTeamList(this.props.activeTeamMember)
      }
    }

    loadWithoutDelay = () => {
      if (this.props.activeTeamMember) {
        this.props.actions.getTeamListWithoutDelay(this.props.activeTeamMember)
      }
    }

    componentDidMount() {
      this.intervalTeamList = setInterval(this.refreshTeamList, 60 * 1000)
    }

    componentDidUpdate(prevProps) {
      if (
        this.props.activeTeamMember !== prevProps.activeTeamMember &&
        utils.user.getKey(this.props.activeTeamMember) !== utils.user.getKey(prevProps.activeTeamMember)
      ) {
        this.loadWithoutDelay()
      }
      if (prevProps.location.key !== this.props.location.key) {
        const prevQueryParams = qs.parse(prevProps.location.search, { ignoreQueryPrefix: true })
        const queryParams = qs.parse(this.props.location.search, { ignoreQueryPrefix: true })
        if (prevQueryParams.scrollto !== queryParams.scrollto) {
          this.props.actions.scrollAndHighlightElementWithId(queryParams.scrollto)
        }
      }
    }

    componentWillUnmount() {
      clearInterval(this.intervalTeamList)
      this.intervalTeamList = undefined
    }

    refreshTeamList = async () => {
      const { actions } = this.props
      const { getNotifications } = actions

      // refresh notifications first to avoid flashing unnotified->notified tasks
      await getNotifications()

      this.load()
    }

    onComplete = ({ id, paramName, value }) => {
      this.props.actions.updateTaskPartiallyById(id, { [paramName]: value })
      this.props.actions.toggleCompleteTaskRequest(id, value)
    }

    patchers = {}

    onChange = (taskId, { paramName, value, method = 'change' }) => {
      let task = this.props.actions.findById(taskId)

      const { params, body } = utils.task.computePatchRequest(task, { paramName, value, method })

      let newTask = utils.task.computeTaskOnChange(task, {
        paramName: paramName,
        value: value,
        method: method
      })
      this.props.actions.updateTask(newTask)
      if (!this.patchers[taskId]) {
        this.patchers[taskId] = new utils.TaskPatcher(constants.delayBeforePatch.default)
      }
      return new Promise((resolve, reject) => {
        this.patchers[taskId].put(paramName, method, () => {
          return this.props.actions
            .patchTaskRequest(task.id, params, body)
            .then(args => {
              resolve(args)
              const activeTeamMember = this.props.activeTeamMember
              if (activeTeamMember) {
                this.props.actions.getTeamList(activeTeamMember)
              }
            })
            .catch(reject)
        })
      })
    }

    onClick = async (todoItemId, type) => {
      if (type !== 'task') {
        return
      }
      await this.props.actions.openPanel(todoItemId)
      const numNotifs = await this.props.actions.markTaskAsRead(todoItemId)
      if (numNotifs > 0) {
        this.props.actions.getTeamList(this.props.activeTeamMember)
        this.props.actions.getTeamMembers()
      }
    }

    onMarkAllAsRead = async () => {
      await this.props.actions.markTeamTasksWithUpdatesAsRead()
      const activeTeamMember = this.props.activeTeamMember
      if (activeTeamMember) {
        this.props.actions.getTeamList(activeTeamMember)
      }
      this.props.actions.getTeamMembers()
    }

    onAddUsers = async users => {
      try {
        await teamlist.addTeamMembers(users)
      } catch (err) {
        console.error(err)
      }
      this.props.actions.getTeamMembers()
    }

    render() {
      let teamGroups = cloneDeep(groups)
      if (this.props.teamItems['updates'].list.length > 0) {
        teamGroups = ['updates'].concat(teamGroups)
      }

      return (
        <Component
          actions={this.props.actions}
          teamItems={this.props.teamItems}
          activeTeamMember={this.props.activeTeamMember}
          onComplete={this.onComplete}
          onChange={this.onChange}
          onClick={this.onClick}
          onMarkAllAsRead={this.onMarkAllAsRead}
          highlightIds={this.props.highlightIds}
          groups={teamGroups}
          onAddUsers={this.onAddUsers}
          hasNoMember={this.props.hasNoMember}
          hasNoTask={this.props.hasNoTask}
          itemsLoaded={this.props.itemsLoaded}
        />
      )
    }
  }

  const containerRedux = connect(
    mapStateToProps,
    mapDispatchToProps,
    null,
    {
      pure: true
    }
  )(TeamlistPageContainer)

  return containerRedux
}



// WEBPACK FOOTER //
// ./src/pages/teamlist/teamPage/container.js